import { useEffect, useLayoutEffect, useState } from "react";
import type { JSX, ReactNode } from "react";

import { DemoSeedSplash } from "../components/onboarding/DemoSeedSplash";
import { BrandMark, Icon } from "../components/ui";
import { useDemoSeedStatus } from "../hooks/useDemoSeedStatus";
import { DemoSeedProfileContext } from "../hooks/demoSeed/profileContext";
import { useAuthStore } from "../store/authStore";
import {
  LOCAL_WORKSPACE_ID,
  getCurrentWorkspace,
  getWorkspaceById,
  useWorkspaceStore,
} from "../store/workspaceStore";
import magnisMascotUrl from "../assets/magnis-mascot-f2.png";
import magnisLogoRoundedUrl from "../../../desktop/icon-rounded.png";

interface LoginGateProps {
  readonly children: ReactNode;
}

/**
 * Wraps the post-login app render with a cloud-demo seed splash. While
 * the bearer-protected `/api/me/profile` endpoint reports
 * `demo_seed_status === 'seeding'`, the splash takes the screen and
 * `children` is held back; for any other status (including `null`,
 * the production / desktop path) `children` renders immediately.
 */
function DemoSeedGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const onboarding = useDemoSeedStatus();
  const [entryPrepared, setEntryPrepared] = useState(false);
  const readyForDemoEntry =
    onboarding.profile.status === "ready" &&
    onboarding.profile.datasetVersion !== null &&
    !onboarding.profile.welcomeRequired;
  useLayoutEffect(() => {
    if (!readyForDemoEntry || entryPrepared) return;
    // @tested-by: tst_fe_onboarding_016, tst_fe_scn_cloud_demo_040
    // @invariant: every hosted-demo app entry starts at a fresh chat draft,
    // regardless of the hash retained from the previous browser session.
    const target =
      `${window.location.pathname}${window.location.search}#/episodes/new`;
    window.history.replaceState(window.history.state, "", target);
    setEntryPrepared(true);
  }, [entryPrepared, readyForDemoEntry]);

  const gated =
    onboarding.loading ||
    onboarding.requestError !== null ||
    onboarding.profile.status === "seeding" ||
    onboarding.profile.status === "failed" ||
    (onboarding.profile.status === "ready" &&
      onboarding.profile.welcomeRequired) ||
    (readyForDemoEntry && !entryPrepared);
  if (gated) {
    return (
      <DemoSeedSplash
        loading={onboarding.loading || (readyForDemoEntry && !entryPrepared)}
        profile={onboarding.profile}
        requestError={onboarding.requestError}
        actionError={onboarding.actionError}
        actionPending={onboarding.actionPending}
        onAcknowledge={() => {
          void onboarding.acknowledge();
        }}
        onRetry={() => {
          void onboarding.retry();
        }}
        onRefresh={() => {
          void onboarding.refresh();
        }}
      />
    );
  }
  return (
    <DemoSeedProfileContext.Provider value={onboarding.profile}>
      {children}
    </DemoSeedProfileContext.Provider>
  );
}

interface GoogleStartResponse {
  readonly authorize_url: string;
  readonly state: string;
}

interface WorkspaceAuthResult {
  readonly type?: string;
  readonly workspaceId?: string;
  readonly success?: boolean;
  readonly token?: string;
  readonly user?: {
    readonly id: string;
    readonly name: string;
    readonly surname: string | null;
    readonly email: string | null;
  };
  readonly error?: string;
}

const WORKSPACE_AUTH_EVENT = "workspace-auth-result";
const CLOSED_BETA_ACCESS_MESSAGE =
  "Magnis Cloud is currently in closed beta. This Google account does not have access yet.";

function workspaceAuthErrorMessage(error: string | undefined): string {
  // @tested-by: tst_fe_login_gate_010
  if (error === "google_account_not_allowed") {
    return CLOSED_BETA_ACCESS_MESSAGE;
  }
  return error ?? "Google sign-in failed.";
}

async function openWorkspaceOAuthWindow(url: string): Promise<Window | null> {
  if ("__TAURI__" in window || "__TAURI_INTERNALS__" in window) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("open_oauth_window", { url });
    return window;
  }
  return window.open(url, "magnis-cloud-google", "width=520,height=720");
}

export function LoginGate({ children }: LoginGateProps): JSX.Element {
  const workspaceReady = useWorkspaceStore((state) => state.ready);
  useWorkspaceStore((state) => state.selectedWorkspaceId);
  const selectWorkspace = useWorkspaceStore((state) => state.selectWorkspace);
  const login = useAuthStore((s) => s.login);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const applySessionForWorkspace = useAuthStore((s) => s.applySessionForWorkspace);
  const currentWorkspace = getCurrentWorkspace();
  const currentWorkspaceId = currentWorkspace?.id ?? null;
  const currentWorkspaceAuthMethod = currentWorkspace?.authMethod ?? null;
  const localWorkspace = getWorkspaceById(LOCAL_WORKSPACE_ID);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bootstrappingOpenAuth, setBootstrappingOpenAuth] = useState(false);
  const canReturnToLocalWorkspace = localWorkspace != null;
  const needsOpenWorkspaceBootstrap =
    workspaceReady &&
    currentWorkspaceId === LOCAL_WORKSPACE_ID &&
    currentWorkspaceAuthMethod === "open" &&
    (token === null || user?.email != null);
  const showsManagedCloudPaywall =
    workspaceReady &&
    currentWorkspace != null &&
    currentWorkspaceAuthMethod !== "open" &&
    token === null;

  useEffect(() => {
    if (!showsManagedCloudPaywall) return;

    const previousTheme = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = "light";

    return (): void => {
      if (previousTheme) {
        document.documentElement.dataset.theme = previousTheme;
        return;
      }
      delete document.documentElement.dataset.theme;
    };
  }, [showsManagedCloudPaywall]);

  useEffect(() => {
    if (!workspaceReady || currentWorkspaceAuthMethod == null) return;
    if (currentWorkspaceAuthMethod !== "open") {
      setBootstrappingOpenAuth(false);
      return;
    }
    if (!needsOpenWorkspaceBootstrap) {
      setBootstrappingOpenAuth(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setBootstrappingOpenAuth(true);
    setError(null);

    void login()
      .then(() => {
        if (cancelled) return;
        setBootstrappingOpenAuth(false);
      })
      .catch((authError: unknown) => {
        if (cancelled) return;
        setBootstrappingOpenAuth(false);
        setError(
          authError instanceof Error
            ? authError.message
            : "Local workspace sign-in failed.",
        );
      });

    return (): void => {
      cancelled = true;
    };
  }, [
    workspaceReady,
    currentWorkspaceId,
    currentWorkspaceAuthMethod,
    needsOpenWorkspaceBootstrap,
    login,
  ]);

  useEffect(() => {
    let unlistenTauri: (() => void) | null = null;

    const handleWorkspaceAuthResult = (data: WorkspaceAuthResult): void => {
      if (data.type !== "workspace_auth_result") return;
      if (data.workspaceId == null) {
        setSubmitting(false);
        setError("Google sign-in returned no workspace.");
        return;
      }
      if (data.success && data.token && data.user) {
        applySessionForWorkspace(data.workspaceId, data.token, data.user);
        setSubmitting(false);
        if (data.workspaceId === currentWorkspaceId) {
          setError(null);
        }
        return;
      }
      setSubmitting(false);
      if (data.workspaceId !== currentWorkspaceId) {
        return;
      }
      setError(workspaceAuthErrorMessage(data.error));
    };

    const onMessage = (event: MessageEvent): void => {
      handleWorkspaceAuthResult(event.data as WorkspaceAuthResult);
    };

    const onStorage = (event: StorageEvent): void => {
      if (event.key !== "workspace_auth_result" || !event.newValue) return;
      try {
        handleWorkspaceAuthResult(JSON.parse(event.newValue) as WorkspaceAuthResult);
      } catch {
        setSubmitting(false);
        setError("Google sign-in failed.");
      } finally {
        localStorage.removeItem("workspace_auth_result");
      }
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    if ("__TAURI__" in window || "__TAURI_INTERNALS__" in window) {
      void import("@tauri-apps/api/event")
        .then(({ listen }) => listen<WorkspaceAuthResult>(
          WORKSPACE_AUTH_EVENT,
          (event) => {
            handleWorkspaceAuthResult(event.payload);
          },
        ))
        .then((unlisten) => {
          unlistenTauri = unlisten;
        })
        .catch(() => {
          setSubmitting(false);
          setError("Google sign-in bridge is unavailable.");
        });
    }
    return (): void => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      unlistenTauri?.();
    };
  }, [applySessionForWorkspace, currentWorkspaceId]);

  if (!workspaceReady || currentWorkspace == null) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary text-content-secondary">
        Loading workspace...
      </div>
    );
  }

  if (currentWorkspace.authMethod === "open") {
    if (!needsOpenWorkspaceBootstrap && token !== null) {
      return <>{children}</>;
    }
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary text-content-secondary">
        {error ?? (bootstrappingOpenAuth ? "Starting local workspace..." : "Loading workspace...")}
      </div>
    );
  }

  if (token !== null) {
    return <DemoSeedGate>{children}</DemoSeedGate>;
  }

  async function handleContinueWithGoogle(): Promise<void> {
    const workspace = currentWorkspace;
    if (workspace?.apiBaseUrl == null) {
      setError("Managed cloud is not configured.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const redirectUri = `${window.location.origin}/oauth/callback`;
      const response = await fetch(
        `${workspace.apiBaseUrl}/api/auth/google/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ redirect_uri: redirectUri }),
        },
      );
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || response.statusText);
      }
      const payload = (await response.json()) as GoogleStartResponse;
      localStorage.setItem("workspace_google_state", payload.state);
      localStorage.setItem(
        "workspace_google_base_url",
        workspace.apiBaseUrl,
      );
      localStorage.setItem("workspace_google_workspace_id", workspace.id);
      const popup = await openWorkspaceOAuthWindow(payload.authorize_url);
      if (!popup) {
        throw new Error("Could not open Google sign-in window.");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Google sign-in failed.");
      setSubmitting(false);
      return;
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f4efe5] px-4">
      <div
        className="relative z-10 w-full max-w-lg rounded-[32px] border border-[#d5ccb9] bg-[#fbf8f2] px-8 py-10 shadow-[0_24px_80px_rgba(27,31,20,0.12)]"
        data-testid="magnis-cloud-entry"
      >
        <div className="flex justify-center">
          <img
            src={magnisLogoRoundedUrl}
            alt="Magnis"
            className="h-16 w-16 rounded-[20px] object-cover shadow-[0_10px_30px_rgba(27,31,20,0.18)]"
          />
        </div>
        <h1 className="mt-6 text-center text-3xl font-semibold tracking-[-0.03em] text-[#172018]">
          Connect to Magnis Cloud
        </h1>

        <button
          type="button"
          onClick={() => { void handleContinueWithGoogle(); }}
          disabled={submitting}
          className="mt-8 flex w-full items-center justify-center gap-3 rounded-2xl border border-[#d7d2c4] bg-white px-5 py-4 text-base font-medium text-[#172018] shadow-sm transition-colors hover:bg-[#f7f5ef] disabled:opacity-60"
        >
          <BrandMark brand="google" size={28} />
          <span>{submitting ? "Opening Google..." : "Continue with Google"}</span>
        </button>

        {error ? (
          <p
            role="alert"
            className="mt-4 rounded-2xl border border-[#d5ccb9] bg-[#f1ece1] px-4 py-3 text-center text-sm text-[#526052]"
          >
            {error}
          </p>
        ) : null}

        {canReturnToLocalWorkspace ? (
          <button
            type="button"
            onClick={() => { void selectWorkspace(LOCAL_WORKSPACE_ID); }}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border-none bg-transparent px-4 py-2 text-sm text-[#526052] transition-colors hover:bg-[#f1ece1]"
          >
            <Icon name="arrow-left" size={14} />
            <span>Use Local Workspace</span>
          </button>
        ) : null}
      </div>

      <img
        src={magnisMascotUrl}
        alt="Magnis mascot"
        className="pointer-events-none absolute bottom-4 right-4 hidden w-28 select-none sm:block md:bottom-8 md:right-8 md:w-36 lg:w-44"
      />
    </div>
  );
}
